/**
 * « Mes autres jeux » — vitrine croisée sous Progression (demande de Benoit,
 * 18 août 2026).
 *
 * Un encart sobre en pied de section : la Progression reste le lieu des objectifs du
 * joueur, la vitrine s'y glisse sans lui voler la vedette. Les entrées viennent de
 * `config/promo.ts` — remplacer les exemples par les vrais jeux ne touche que la
 * configuration.
 *
 * Chaque lien s'ouvre dans un NOUVEL onglet avec `rel="noopener noreferrer"` : la
 * page ouverte ne doit ni voler l'onglet du jeu (le joueur revient), ni recevoir une
 * référence vers notre fenêtre.
 */

import { PROMO_GAMES } from '@/config/promo';
import { APP_VERSION } from '@/config/changelog';
import { t } from '@/i18n';
import { createPanel } from './components';

export interface PromoSectionHandle {
  readonly el: HTMLElement;
  destroy(): void;
}

export function createPromoSection(): PromoSectionHandle {
  const title = document.createElement('h3');
  title.className = 'promo-section__title';
  title.textContent = t('promo.title');

  const hint = document.createElement('p');
  hint.className = 'promo-section__hint';
  hint.textContent = t('promo.hint');

  const list = document.createElement('div');
  list.className = 'promo-section__list';

  // Sans jeu à montrer, la section le DIT — pas de cartes d'exemple qui se font
  // passer pour de vrais jeux (demande de Benoit, 18 août).
  if (PROMO_GAMES.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'promo-section__empty';
    empty.textContent = t('promo.empty');
    list.append(empty);
  }

  for (const game of PROMO_GAMES) {
    const card = document.createElement('a');
    card.className = 'promo-card';
    card.href = game.url;
    card.target = '_blank';
    card.rel = 'noopener noreferrer';
    card.dataset.game = game.id;

    /*
      Icône si le jeu en a une, emoji sinon. Les deux sont DÉCORATIFS : le nom du jeu
      suit immédiatement, et un lecteur d'écran n'a rien à faire d'une vignette.

      L'URL porte la VERSION, comme tout ce qui vit dans `public/` (règle 20) : sans
      elle, le service worker servirait l'ancienne icône trente jours après un
      changement de logo.

      ⚠️ Pas `spriteUrl` : ce helper est spécialisé pour les sprites de bâtiment — il
      préfixe `/assets/buildings/` et ajoute `.png`. Il fabriquait
      `/assets/buildings/promo/tradopia.svg.png`, et l'image ne chargeait pas
      (`naturalWidth` à 0, sans la moindre erreur de page). Un helper qui porte une
      convention dans son nom ne se réutilise pas hors de cette convention.
    */
    const emoji = document.createElement('span');
    emoji.className = 'promo-card__emoji';
    if (game.icon === undefined) {
      emoji.textContent = game.emoji;
    } else {
      const image = document.createElement('img');
      image.src = `/${game.icon}?v=${APP_VERSION}`;
      image.alt = '';
      image.className = 'promo-card__icon';
      emoji.append(image);
    }
    emoji.setAttribute('aria-hidden', 'true');

    const body = document.createElement('span');
    body.className = 'promo-card__body';

    const name = document.createElement('b');
    name.className = 'promo-card__name';
    name.textContent = game.name;

    const tagline = document.createElement('span');
    tagline.className = 'promo-card__tagline';
    tagline.textContent = t(game.taglineKey);

    body.append(name, tagline);

    const action = document.createElement('span');
    action.className = 'promo-card__action';
    action.textContent = t('promo.play');

    card.append(emoji, body, action);
    list.append(card);
  }

  const panel = createPanel({ children: [title, hint, list] });
  panel.el.classList.add('promo-section');

  return {
    el: panel.el,
    destroy() {
      panel.el.remove();
    },
  };
}
